/**
 * COMO SE LEE UNA FILA DEL HISTORIAL DE ACTIVIDAD.
 *
 * Asniel Lopez (Pomares Insurance Group), 2026-09-18: *"me gustaria saber como
 * identificar en el HISTORIAL DE ACTIVIDADES cada que subo un documento con
 * hora, fecha y persona que lo subio... no se si salen como POLIZA AGREGADA -
 * carpeta o de otra manera"*.
 *
 * Tenia razon en no saberlo: la fila no lo decia. Las acciones genericas
 * —`created`, `updated`, `deleted`— las escriben media docena de rutas
 * distintas, asi que por si solas no significan nada: una subida de documento y
 * un cliente nuevo salian las dos como "✨ Creado". Lo que desambigua es la
 * ENTIDAD de la fila, y mirarla arregla tambien las filas VIEJAS, que siguen
 * guardadas con la accion generica y no se pueden reescribir.
 *
 * Vive fuera del componente para poder ejecutarlo en una prueba: el texto que
 * lee el agente es justo lo que no se puede comprobar leyendo el codigo.
 */
// Maps a raw ActivityLog `action` value to a human-readable label + icon.
// Covers everything we currently emit in the backend (client.*, policy.*,
// document.*, signature.*, task.*, etc.). Unknown actions fall through to
// a generic "✏️  <action>" rendering rather than dropping the row.
export const HISTORY_ACTION_LABELS = {
  created: { en: 'Created', es: 'Creado', icon: '✨' },
  updated: { en: 'Updated', es: 'Actualizado', icon: '✏️' },
  deleted: { en: 'Deleted', es: 'Eliminado', icon: '🗑️' },
  restored: { en: 'Restored', es: 'Restaurado', icon: '♻️' },
  form_submission: { en: 'Form submission', es: 'Envío de formulario', icon: '📝' },
  registered: { en: 'Registered', es: 'Registrado', icon: '👋' },
  'client.viewed': { en: 'Profile viewed', es: 'Ficha consultada', icon: '👁️' },
  'client.merged': { en: 'Client merged', es: 'Cliente combinado', icon: '🔀' },
  'client.tagged': { en: 'Tags updated', es: 'Etiquetas actualizadas', icon: '🏷️' },
  'ssn.reveal': { en: 'SSN revealed', es: 'SSN revelado', icon: '🔓' },
  'ssn.reveal_denied': { en: 'SSN reveal denied', es: 'SSN revelado denegado', icon: '🔒' },
  'phi.decrypt': { en: 'PHI decrypted', es: 'PHI desencriptado', icon: '🔓' },
  policy_created: { en: 'Policy added', es: 'Póliza agregada', icon: '📄' },
  policy_updated: { en: 'Policy updated', es: 'Póliza actualizada', icon: '📄' },
  policy_deleted: { en: 'Policy removed', es: 'Póliza eliminada', icon: '📄' },
  'policy.created': { en: 'Policy added', es: 'Póliza agregada', icon: '📄' },
  'policy.updated': { en: 'Policy updated', es: 'Póliza actualizada', icon: '📄' },
  'policy.cancellation_scheduled': { en: 'Cancellation scheduled', es: 'Cancelación programada', icon: '⏳' },
  'policy.cancellation_unscheduled': { en: 'Scheduled cancellation removed', es: 'Cancelación programada quitada', icon: '↩️' },
  'policy.deleted': { en: 'Policy removed', es: 'Póliza eliminada', icon: '📄' },
  // PR #370: expanded coverage — every mutation now emits an audit row
  'task.created': { en: 'Task added', es: 'Tarea creada', icon: '✅' },
  'task.updated': { en: 'Task updated', es: 'Tarea actualizada', icon: '✅' },
  'note.created': { en: 'Note added', es: 'Nota creada', icon: '📝' },
  'note.updated': { en: 'Note updated', es: 'Nota actualizada', icon: '📝' },
  'note.deleted': { en: 'Note deleted', es: 'Nota eliminada', icon: '📝' },
  'appointment.created': { en: 'Appointment scheduled', es: 'Cita programada', icon: '📅' },
  'appointment.updated': { en: 'Appointment updated', es: 'Cita actualizada', icon: '📅' },
  'eligibility.created': { en: 'Eligibility added', es: 'Elegibilidad creada', icon: '📋' },
  'eligibility.updated': { en: 'Eligibility updated', es: 'Elegibilidad actualizada', icon: '📋' },
  'eligibility.deleted': { en: 'Eligibility removed', es: 'Elegibilidad eliminada', icon: '📋' },
  'agency.logo_updated': { en: 'Agency logo updated', es: 'Logo de agencia actualizado', icon: '🎨' },
  'agency.logo_cleared': { en: 'Agency logo cleared', es: 'Logo de agencia eliminado', icon: '🎨' },
  'document.read_sas_issued': { en: 'Document accessed', es: 'Documento accedido', icon: '👁️' },
  'document.renamed': { en: 'Document renamed', es: 'Documento renombrado', icon: '📎' },
  'document.updated': { en: 'Document updated', es: 'Documento actualizado', icon: '📎' },
  'document.portal_upload': { en: 'Document uploaded by client (portal)', es: 'Documento subido por el cliente (portal)', icon: '📥' },
  'signature.sent': { en: 'Signature requested', es: 'Firma solicitada', icon: '✍️' },
  'signature.signed': { en: 'Signed', es: 'Firmado', icon: '✍️' },
  'quote.converted': { en: 'Quote converted', es: 'Cotización convertida', icon: '💼' },
  'invoice.created': { en: 'Invoice created', es: 'Factura creada', icon: '🧾' },
};

// Las acciones genericas ('created', 'updated', 'deleted') las escriben media
// docena de rutas distintas, asi que por si solas no dicen NADA: una subida de
// documento y un cliente nuevo salian las dos como "✨ Creado". Asniel Lopez,
// 2026-09-18: *"no se si salen como POLIZA AGREGADA - carpeta o de otra
// manera"*. Tenia razon en no saberlo: la fila no lo decia.
//
// La entidad es la que lo desambigua, y esto arregla tambien las filas VIEJAS,
// que siguen guardadas con la accion generica y no se pueden reescribir.
export const GENERIC_ACTION_BY_ENTITY = {
  document: {
    created: { en: 'Document uploaded', es: 'Documento subido', icon: '📎' },
    updated: { en: 'Document updated', es: 'Documento actualizado', icon: '📎' },
    deleted: { en: 'Document deleted', es: 'Documento eliminado', icon: '🗑️' },
  },
};

export function actionMeta(action, lang, entityType) {
  const porEntidad = GENERIC_ACTION_BY_ENTITY[entityType]?.[action];
  if (porEntidad) return { label: lang === 'es' ? porEntidad.es : porEntidad.en, icon: porEntidad.icon };
  const meta = HISTORY_ACTION_LABELS[action];
  if (meta) return { label: lang === 'es' ? meta.es : meta.en, icon: meta.icon };
  // Strip "<entity>." prefix and title-case so "policy.renewed" → "Policy renewed".
  const pretty = String(action || 'changed')
    .replace(/[._]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return { label: pretty.charAt(0).toUpperCase() + pretty.slice(1), icon: '•' };
}
